<script setup lang="ts">
import {ref, computed, onMounted, onUnmounted} from 'vue';
import {POST} from '../modules/fetch.ts';
import {SvgIcon} from '../svg.ts';
import {fetchDesignReviewPages} from '../features/design-review-pages.ts';
import {FILE_DIFF_STATUS, type FileDiffStatus} from '../allspice_app/types/file_tree.ts';
import SmartReviewPageSelector from './SmartReviewPageSelector.vue';
import SmartReviewVariantSelector from './SmartReviewVariantSelector.vue';
import {matchSavedSelection} from './smart-review-utils.ts';

type SavedEntry = {
  page_id: string;
  name: string;
  selected: boolean;
};

type SelectionPayload = Record<string, {page_id: string; name: string; selected: boolean}>;

type PageInfo = {
  name: string;
  pageId: string;
  number: number;
  status: FileDiffStatus;
};

type VariantInfo = {
  id: string;
  name: string;
};

// Mirror of the Hub's pull_model.SmartReviewRunState (run_state column).
type RunStateInfo = {
  head_sha: string;
  state: string;
  reviewed_pages: string[];
  last_run_id: number;
};

const props = defineProps<{
  owner: string;
  repo: string;
  pullIndex: string;
  requestUrl: string;
  updateUrl: string;
  stopUrl: string;
  savedSelection: Record<string, SavedEntry>;
  savedVariant: VariantInfo | null;
  // The preferred part-number attribute is a repository-wide setting (see the
  // repo Settings page). It is shown here read-only with a link to settings.
  preferredPartNumberAttribute: string;
  settingsUrl: string;
  canConfigure: boolean;
  // DRCY runs as an Action; when Actions are disabled for the repo the
  // request can't be dispatched, so the dropdown explains this instead of
  // offering a Start that would fail. `canEnableActions` decides whether to
  // show the link to repo settings (only repo admins can flip the unit).
  actionsEnabled: boolean;
  canEnableActions: boolean;
  actionsSettingsUrl: string;
  headCommit: string;
  baseCommit: string;
  avatarUrl: string;
  isRunning: boolean;
  runState: RunStateInfo | null;
  locale: {
    request: string;
    selectPagesHeader: string;
    pageInputPlaceholder: string;
    selectAll: string;
    loading: string;
    noPages: string;
    loadFailed: string;
    showUnchanged: string;
    start: string;
    startFailed: string;
    running: string;
    runningShort: string;
    variantHeader: string;
    noVariantsNotice: string;
    defaultLabel: string;
    stop: string;
    stopFailed: string;
    resumeReviewedLead: string;
    resumeReviewedTrail: string;
    selectRemaining: string;
    dismiss: string;
    statusCompleted: string;
    pagesReviewed: string;
    preferredPartNumberAttributeHeader: string;
    preferredPartNumberAttributeInferred: string;
    preferredPartNumberAttributeSettingsLink: string;
    actionsDisabled: string;
    actionsDisabledLink: string;
  };
}>();

const rootEl = ref<HTMLElement>();
const dropdownVisible = ref(false);
const isStarting = ref(false);
const startError = ref(false);
const isStopping = ref(false);
const stopError = ref(false);
// The resume hint is a dismissible, non-blocking suggestion; closing it leaves
// the rest of the popup (page selector, variant, Start) fully usable.
const resumeDismissed = ref(false);
const cachedVariant = ref<VariantInfo | null>(props.savedVariant);

// Persisted progress only applies while it describes the current head commit;
// once the head moves the stored state is stale and a fresh run starts over.
const runStateAtHead = computed<RunStateInfo | null>(() =>
  props.runState && props.runState.head_sha === props.headCommit ? props.runState : null,
);
const reviewedCount = computed(() => runStateAtHead.value?.reviewed_pages?.length ?? 0);
// Page IDs already reviewed at this head. Run-state keys are
// ``"{schematic_path}:{page_id}"``; the selector keys pages by ``page_id``
// alone, so we match on the trailing segment (consistent with how the rest of
// this component treats ``pageId`` as the unique key).
const reviewedPageIds = computed<Set<string>>(() => {
  const ids = new Set<string>();
  for (const key of runStateAtHead.value?.reviewed_pages ?? []) {
    ids.add(key.slice(key.lastIndexOf(':') + 1));
  }
  return ids;
});
// A run that stopped short of completion (time limit, failure, or a manual
// Stop) with pages already done can be picked up where it left off — we hint
// the reviewer to narrow the selection to the pages that aren't done yet.
// Only the explicit `stopped` lifecycle state qualifies: a lingering `running`
// state must not offer Resume (it would invite re-triggering a run that may
// still be in flight if review-request detection is briefly stale).
const resumable = computed(() =>
  !props.isRunning &&
  runStateAtHead.value !== null &&
  runStateAtHead.value.state === 'stopped' &&
  reviewedCount.value > 0,
);
const completedAtHead = computed(() =>
  !props.isRunning &&
  runStateAtHead.value !== null &&
  runStateAtHead.value.state === 'completed',
);

const pages = ref<PageInfo[]>([]);
const selectedPageIds = ref<Set<string>>(new Set());
const isLoadingPages = ref(true);
const loadPagesError = ref(false);
let pagesLoaded = false;

const formDisabled = computed(() => props.isRunning || isStarting.value);
const noneChecked = computed(() => selectedPageIds.value.size === 0);
const startDisabled = computed(() => formDisabled.value || noneChecked.value);

// With no unchanged page listed, checking everything is the default, so send
// no selection and DRCY reviews whatever changed when it runs.
const selectionPayload = computed<SelectionPayload | null>(() => {
  const isDefault = pages.value.every(
    (p) => p.status !== FILE_DIFF_STATUS.UNCHANGED && selectedPageIds.value.has(p.pageId),
  );
  if (isDefault) return null;

  const result: SelectionPayload = {};
  for (const p of pages.value) {
    result[p.pageId] = {
      page_id: p.pageId,
      name: p.name,
      selected: selectedPageIds.value.has(p.pageId),
    };
  }
  return result;
});

function toggleDropdown() {
  dropdownVisible.value = !dropdownVisible.value;
}

function onClickOutside(e: MouseEvent) {
  if (!dropdownVisible.value) return;
  const target = e.target as Node | null;
  // Ignore clicks whose target was removed from the DOM while handling them
  // (e.g. dismissing the resume hint removes the very button that was
  // clicked): a detached node reports as "outside" and would wrongly close
  // the panel. Only act on clicks that are still in the document and outside.
  if (!target || !target.isConnected) return;
  if (rootEl.value && !rootEl.value.contains(target)) {
    dropdownVisible.value = false;
  }
}

async function loadPages() {
  isLoadingPages.value = true;
  loadPagesError.value = false;
  try {
    await fetchPages();
  } catch {
    loadPagesError.value = true;
  } finally {
    isLoadingPages.value = false;
  }
}

async function fetchPages() {
  if (pagesLoaded) return;

  const results = await fetchDesignReviewPages(props.owner, props.repo, props.pullIndex);

  let pageNumber = 1;
  const loadedPages: PageInfo[] = [];
  const selected = new Set<string>();
  for (const p of results) {
    loadedPages.push({name: p.name, pageId: p.pageId, status: p.status, number: pageNumber++});
    // Changed pages stay selected by default; unchanged pages are opt-in
    // (unselected by default). A saved selection still wins for pages it
    // covers.
    const defaultSelected = p.status !== FILE_DIFF_STATUS.UNCHANGED;
    const isSelected = matchSavedSelection(
      props.savedSelection, p.pageId, p.name, defaultSelected,
    );
    if (isSelected) selected.add(p.pageId);
  }
  pages.value = loadedPages;
  selectedPageIds.value = selected;
  pagesLoaded = true;
}

function onVariantChange(variant: VariantInfo | null) {
  cachedVariant.value = variant;
}

onMounted(() => {
  document.addEventListener('click', onClickOutside);
  loadPages();
});

onUnmounted(() => {
  document.removeEventListener('click', onClickOutside);
});

async function startDrcy() {
  startError.value = false;
  isStarting.value = true;
  try {
    // POST() resolves with the Response even on non-2xx (it doesn't throw), so
    // check .ok explicitly — otherwise a failed save/request would still reload
    // and hide the error from the user.
    const saved = await POST(props.updateUrl, {data: {pages: selectionPayload.value, variant: cachedVariant.value}});
    if (!saved.ok) throw new Error(`save config failed: ${saved.status}`);
    const requested = await POST(props.requestUrl);
    if (!requested.ok) throw new Error(`request review failed: ${requested.status}`);
    window.location.reload();
  } catch (err) {
    console.error('Failed to start DRCY', err);
    startError.value = true;
    isStarting.value = false;
  }
}

async function stopDrcy() {
  stopError.value = false;
  isStopping.value = true;
  try {
    // Server-side this cancels the in-flight DRCY Actions run and withdraws the
    // review request; whatever pages finished stay reviewed and resumable.
    // POST() doesn't throw on non-2xx, so check .ok to surface stopFailed.
    const res = await POST(props.stopUrl);
    if (!res.ok) throw new Error(`stop failed: ${res.status}`);
    window.location.reload();
  } catch (err) {
    console.error('Failed to stop DRCY', err);
    stopError.value = true;
    isStopping.value = false;
  }
}

function selectRemaining() {
  // Narrow the selection to the in-scope pages DRCY hasn't reviewed yet at this
  // head, so a normal Start picks up where the previous run left off. Purely a
  // client-side selection change — the reviewer still reviews and clicks Start.
  const reviewed = reviewedPageIds.value;
  const next = new Set<string>();
  for (const p of pages.value) {
    if (reviewed.has(p.pageId)) continue;
    // Mirror the default in-scope rule used when seeding the selection:
    // changed pages are in scope, unchanged ones are opt-in (and a saved
    // selection still wins where it covers a page).
    const inScope = matchSavedSelection(
      props.savedSelection, p.pageId, p.name, p.status !== FILE_DIFF_STATUS.UNCHANGED,
    );
    if (inScope) next.add(p.pageId);
  }
  selectedPageIds.value = next;
  resumeDismissed.value = true;
}
</script>

<template>
  <div ref="rootEl" class="tw:relative tw:inline-block">
    <a class="ui small basic button" href="#" @click.prevent="toggleDropdown">
      <span v-if="isRunning" class="smart-review-loading"></span>
      <img v-else :src="avatarUrl" width="14" height="14" class="tw:align-text-bottom">
      {{ isRunning ? locale.runningShort : locale.request }}
      <SvgIcon name="octicon-triangle-down" :size="14"/>
    </a>

    <div v-show="dropdownVisible" class="smart-review-dropdown">
      <div class="ui segment tw:py-3 tw:my-1 smart-review-dropdown-panel">
        <div v-if="isRunning">
          <div class="ui info message tw:text-sm tw:my-2">
            {{ locale.running }}
            <template v-if="reviewedCount > 0">
              <br>{{ reviewedCount }} {{ locale.pagesReviewed }}
            </template>
          </div>
          <div v-if="stopError" class="ui negative message tw:text-sm tw:my-2">
            {{ locale.stopFailed }}
          </div>
          <div class="tw:text-right tw:mb-2">
            <button
              type="button"
              class="ui button tiny smart-review-stop-btn"
              :class="{'is-loading': isStopping}"
              :disabled="isStopping"
              @click="stopDrcy"
            >
              {{ locale.stop }}
            </button>
          </div>
        </div>

        <template v-else>
          <div v-if="startError" class="ui negative message tw:text-sm tw:my-2">
            {{ locale.startFailed }}
          </div>

          <div
            v-if="resumable && !resumeDismissed"
            class="ui info message tw:text-sm tw:my-2 smart-review-resume-hint"
          >
            <button
              type="button"
              class="smart-review-resume-dismiss"
              :title="locale.dismiss"
              :aria-label="locale.dismiss"
              @click.stop="resumeDismissed = true"
            >
              <SvgIcon name="octicon-x" :size="14"/>
            </button>
            <div class="tw:pr-4">
              {{ locale.resumeReviewedLead }} {{ reviewedCount }} {{ locale.resumeReviewedTrail }}
            </div>
            <div class="tw:mt-2 tw:text-right">
              <button
                type="button"
                class="ui button tiny smart-review-select-remaining-btn"
                @click.stop="selectRemaining"
              >
                {{ locale.selectRemaining }}
              </button>
            </div>
          </div>
          <div
            v-else-if="completedAtHead"
            class="ui positive message tw:text-sm tw:my-2 smart-review-status"
          >
            {{ locale.statusCompleted }} — {{ reviewedCount }} {{ locale.pagesReviewed }}
          </div>
        </template>

        <div
          v-if="!isRunning && !actionsEnabled"
          class="ui warning message tw:text-sm tw:my-2 smart-review-actions-disabled"
        >
          {{ locale.actionsDisabled }}
          <a v-if="canEnableActions" :href="actionsSettingsUrl">{{ locale.actionsDisabledLink }}</a>
        </div>

        <template v-if="actionsEnabled">
          <SmartReviewPageSelector
            :pages="pages"
            v-model:selected-page-ids="selectedPageIds"
            :is-loading="isLoadingPages"
            :load-error="loadPagesError"
            :disabled="formDisabled"
            :locale="{
              selectPagesHeader: locale.selectPagesHeader,
              pageInputPlaceholder: locale.pageInputPlaceholder,
              selectAll: locale.selectAll,
              loading: locale.loading,
              noPages: locale.noPages,
              loadFailed: locale.loadFailed,
              showUnchanged: locale.showUnchanged,
            }"
          />

          <SmartReviewVariantSelector
            :owner="owner"
            :repo="repo"
            :head-commit="headCommit"
            :base-commit="baseCommit"
            :saved-variant="savedVariant"
            :disabled="formDisabled"
            :locale="{
              variantHeader: locale.variantHeader,
              noVariantsNotice: locale.noVariantsNotice,
              defaultLabel: locale.defaultLabel,
            }"
            @variant-change="onVariantChange"
          />

        <div class="tw:mt-3">
          <div class="tw:font-semibold tw:mb-2">
            {{ locale.preferredPartNumberAttributeHeader }}
          </div>
          <div class="tw:flex tw:items-center tw:gap-2 tw:text-sm">
            <code v-if="preferredPartNumberAttribute">{{ preferredPartNumberAttribute }}</code>
            <span
              v-else
              class="tw:text-muted"
            >{{ locale.preferredPartNumberAttributeInferred }}</span>
            <a
              v-if="canConfigure"
              :href="settingsUrl"
              class="tw:inline-flex tw:items-center tw:gap-1 tw:text-muted"
            >
              <SvgIcon
                name="octicon-gear"
                :size="14"
              />
              {{ locale.preferredPartNumberAttributeSettingsLink }}
            </a>
          </div>
        </div>

          <div class="tw:mt-3 tw:text-right">
            <button
              type="button"
              class="ui button smart-review-start-btn"
              :class="{'is-loading': isStarting}"
              :disabled="startDisabled"
              @click="startDrcy"
            >
              {{ formDisabled ? locale.runningShort : locale.start }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.smart-review-dropdown {
  position: absolute;
  right: 0;
  top: 100%;
  z-index: 100;
  width: 380px;
}

@media (max-width: 767.98px) {
  .smart-review-dropdown {
    left: 0;
    right: auto;
    width: calc(100vw - 2rem);
    max-width: 380px;
  }
}

.smart-review-dropdown-panel {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.smart-review-start-btn {
  background-color: var(--color-primary) !important;
  color: var(--color-white) !important;
}

.smart-review-stop-btn {
  background-color: var(--color-red) !important;
  color: var(--color-white) !important;
}

.smart-review-resume-hint {
  position: relative;
}

.smart-review-resume-dismiss {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-text-light-2);
  line-height: 1;
}

.smart-review-resume-dismiss:hover {
  color: var(--color-text);
}
</style>
